import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import {
    RrsTemplateLabel,
    RrsTemplateLabelCreate,
    RrsTemplateLabelCategory,
} from "server/models"
import {
    StateType,
    useInput,
} from "client/utils"
import {
    StoreType,
    RrsTemplateLabelValidation,

    SaveRrsTemplateLabel,
    CreateRrsTemplateLabelCategories,
    LoadAllRrsTemplateLabels,
    LoadAllRrsTemplateLabelCategories,
    LoadAllRrsTemplateLevels,
    SetRrsTemplateRiskMapGenerated,
    DeleteRrsTemplateLevelsForLabel,
    DeleteRrsTemplateLabelCategoriesByLabel,

    setRrsTemplateEditLoadState,
    setRrsTemplateEdit,
    setRrsTemplateLabelList,
    setRrsTemplateLabelCategoryList,
    setRrsTemplateLevelList,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Textarea,
    Feedback,
    SelectDropdown,
    SelectDropdownItem,
    Empty,
    Button,
    SpinnerIcon,
    TableNestedIds,
    NotAvailable,
} from "client/components"

let isMounted: boolean

interface TemplateLabelFormProps {
    label: RrsTemplateLabel
}

const Form = ({
    label,
}: TemplateLabelFormProps) => {
    const template = useSelector((state: StoreType) => state.rrsTemplate.templateEdit)
    const categoryList = useSelector((state: StoreType) => state.rrsCategory.categoryList)
    const categoryIds = useSelector((state: StoreType) => state.rrsCategory.categoryIds)
    const childParent = useSelector((state: StoreType) => state.rrsCategory.categoryChildParent)

    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [nestedCategoryList, setNestedCategoryList] = useState<any[]>([])
    const [parentCategory, setParentCategory] = useState<string>(null)

    const [categoryId, setCategoryId] = useState(label.categoryId)
    const [name, setName] = useInput(label.name)
    const [shortName, setShortName] = useInput(label.shortName)
    const [description, setDescription] = useInput(label.description)
    const [order, setOrder] = useState(label.order)

    const [validation, setValidation] = useState<RrsTemplateLabelValidation>(null)
    const [saveState, setSaveState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        const nestedList: any[] = []
        const addNested = (nestedIds: readonly TableNestedIds[], level: number) => {
            if (level > 1) return
            nestedIds.forEach((row) => {
                const id = Number(row.id)
                const categoryItem = categoryList[id]
                nestedList.push(
                    <SelectDropdownItem
                        key={id}
                        onClick={() => (level === 1 ? setCategoryId(id) : null)}
                    >
                        <span style={{ paddingLeft: `${level * 30}px` }}>
                            {`${categoryItem.name} `}
                            {level !== 1 && <NotAvailable />}
                        </span>
                    </SelectDropdownItem>,
                )
                addNested(row.nestedIds, level + 1)
            })
        }
        if (categoryIds[0]) addNested(categoryIds[0].nestedIds, 0) // where categoryIds[0] - top level group

        setNestedCategoryList(nestedList)
    }, [categoryList, categoryIds])

    useEffect(() => {
        const parentArray: string[] = []
        const addParent = (parentId: number) => {
            if (parentId) {
                const parent = categoryList[parentId]
                if (parent) parentArray.unshift(parent.shortName.length ? parent.shortName : parent.name)
                addParent(childParent[parentId])
            }
        }
        addParent(categoryId)
        setParentCategory(parentArray.length ? parentArray.join(" > ") : null)
    }, [categoryId, categoryList, childParent])

    const onClose = () => {
        if (!isMounted) return
        if (modals.length && isMounted) dispatch(closeModal())
    }

    const onSave = () => {
        // select only category with nested categories
        const childCategoryIds: number[] = []
        if (categoryId) {
            Object.keys(childParent).forEach((key) => {
                const childId = Number(key)
                const parentId = childParent[childId]
                if (parentId === categoryId) childCategoryIds.push(childId)
            })
            if (!childCategoryIds.length) {
                setValidation({ categoryId: lang.childCategoriesRequired })
                return
            }
        }

        setSaveState(PENDING)

        const newLabelData: RrsTemplateLabelCreate = {
            templateId: template.id,
            categoryId,
            name,
            shortName,
            description,
            order,
        }

        SaveRrsTemplateLabel(
            label.id,
            newLabelData,
            lang,
        ).then((res) => {
            if (res.validation) {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                }
                return
            }

            const promiseArray: Promise<any>[] = []
            const labelId = res.newLabelId || label.id

            // on change parent category
            if (label.categoryId !== categoryId) {
                promiseArray.push(
                    DeleteRrsTemplateLevelsForLabel(labelId), // when label HAS NO nested categories
                    DeleteRrsTemplateLabelCategoriesByLabel(labelId) // when label HAS nested categories
                    .then(() => {
                        // add all nested categories to label
                        const labelCategoryData: RrsTemplateLabelCategory[] = []
                        childCategoryIds.forEach((childCategoryId) => {
                            labelCategoryData.push({
                                labelId,
                                categoryId: childCategoryId,
                            })
                        })
                        return CreateRrsTemplateLabelCategories(labelCategoryData)
                    }),
                )
            }

            Promise.all(promiseArray).then(() => {
                if (isMounted) {
                    setValidation(res.validation)
                    setSaveState(IDLE)
                }
                onClose()

                dispatch(setRrsTemplateEditLoadState(PENDING))
                const nestedPromiseArray: Promise<any>[] = [
                    LoadAllRrsTemplateLabels(template.id).then((labels) => {
                        dispatch(setRrsTemplateLabelList(labels))
                    }),
                ]

                // on change parent category - reload label categories and levels
                if (label.categoryId !== categoryId) {
                    nestedPromiseArray.push(
                        LoadAllRrsTemplateLabelCategories(labelId).then((nestedCategories) => {
                            dispatch(setRrsTemplateLabelCategoryList(labelId, nestedCategories))
                        }),
                        LoadAllRrsTemplateLevels(labelId).then((levels) => {
                            dispatch(setRrsTemplateLevelList(labelId, levels))
                        }),
                    )
                }

                Promise.all(nestedPromiseArray).then(() => {
                    if (!label.id || label.categoryId !== categoryId) { // when new label created or label parent category changed
                        SetRrsTemplateRiskMapGenerated([template.id], false).then(() => { // set riskMapGenerated to "false"
                            dispatch(setRrsTemplateEdit({
                                ...template,
                                riskMapGenerated: false,
                            }))
                            dispatch(setRrsTemplateEditLoadState(IDLE))
                        })
                    } else {
                        dispatch(setRrsTemplateEditLoadState(IDLE))
                    }
                })
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.parentCategory}</Label>
                        <SelectDropdown title={parentCategory || <Empty />}>
                            <SelectDropdownItem onClick={() => setCategoryId(null)}>
                                <Empty />
                            </SelectDropdownItem>
                            { nestedCategoryList }
                        </SelectDropdown>
                        <Feedback>{validation && validation.categoryId}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.shortName}</Label>
                        <Input type="text" value={shortName} onChange={setShortName} />
                        <Feedback>{validation && validation.shortName}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.description}</Label>
                        <Textarea value={description} onChange={setDescription} />
                        <Feedback>{validation && validation.description}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.order}</Label>
                        <Input
                            type="text"
                            value={order !== null ? order : ""}
                            onChange={(e) => {
                                const parsed = parseInt(e.target.value, 10)
                                if (!Number.isNaN(parsed)) setOrder(parsed)
                                else setOrder(null)
                            }}
                        />
                        <Feedback>{validation && validation.order}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12}>
                    <Button
                        variant="success"
                        row
                        onClick={onSave}
                    >
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
